import React, { memo } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import cx from 'classnames'

import { makeStyles } from '@material-ui/core/styles'
import { Button, Divider, ListItem, Typography, Grid, IconButton } from '@material-ui/core'
import { Star, Search, ArrowForwardIos, Whatshot } from '@material-ui/icons'

import { useLiterals } from '~/components/Literals'
import ProductImageThumb from '~/components/ProductImageThumb'

const useStyles = makeStyles((theme) => ({
  listItem: {
    padding: 8,
    gap: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',

    '& .detail': {
      gap: 8,
      display: 'flex',
      alignItems: 'center',

      '& > div': {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
      },
    },
  },
  text: {
    fontSize: 18,
    fontWeight: 'bold',
    ...theme.lineLimit(2),
    '& span': {
      ...theme.lineLimit(2),
      overflow: 'hidden',
    },
  },
  seeMoreDetails: {
    paddingBlock: 6,
    display: 'flex',
    alignItems: 'center',

    fontSize: 14,
    textTransform: 'lowercase',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  featured: {
    '&.new $badge': {
      background: '#e74c3c',
    },
    '&.default $badge': {
      background: '#2980b9',
    },
  },
  badge: {
    color: '#fff',
    padding: '2px 6px 0',
    borderRadius: 9999,

    display: 'flex',
    justifyContent: 'center',

    textTransform: 'uppercase',
    '& svg': {
      fontSize: '15px',
    },

    [theme.breakpoints.down('xs')]: {
      fontSize: 10,
    },
  },
  buttonSearch: {
    backgroundColor: '#F2F2F2',
    textTransform: 'none',
    paddingInline: 16,

    '&:hover': {
      backgroundColor: '#EEEBEB',
    },

    '& span': {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      '& svg': {
        width: 20,
        height: 20,
      },
      '& span': {
        fontFamily: 'Roboto, sans-serif',
        fontWeight: 500,
        fontSize: 14,
        color: '#322B2D',
      },
    },

    [theme.breakpoints.down('sm')]: {
      '&': {
        minWidth: 44,
        borderRadius: 9999,
        padding: 0,
      },
      '& span > span': {
        display: 'none',
      },
    },
  },
}))

function Product({ product, onSelectProduct, position, mode }) {
  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()

  const { getLiteral } = useLiterals()
  const { featured, name, description, gtin } = product

  const handleViewProduct = async (e, product) => {
    e.preventDefault()
    e.stopPropagation()

    history.push(`/produto/${product.gtin}/detalhes`, {
      fromApp: true,
      prevPath: location.pathname,
    })
  }

  const handleCheckGeolocation = async (e, product) => {
    e.stopPropagation()

    if (!position.user?.lat || !position.user?.lng) {
      position.handlerShowPositionDialog(true)
      return
    }

    handleViewProduct(e, product)
  }

  return (
    <>
      <ListItem
        data-cy="product"
        onClick={() => onSelectProduct(product)}
        aria-label={`${getLiteral('2036')} ${name}`}
        className={cx(classes.listItem, {
          [classes.featured]: !!featured,
          [featured ? featured.type : '']: !!featured,
        })}
        data_analytics_category="onde-comprar"
        data_analytics_action="search_product"
        data_analytics_product_id={gtin}
        data_analytics_product_name={name}
        data_analytics_product_size={name}
        data_analytics_product_type={name}
        data_analytics_local={position?.user?.city}
      >
        <Grid className="detail">
          <ProductImageThumb product={product} />

          <Grid>
            <Grid style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {featured && featured.type === 'new' && (
                <Typography variant="caption" component="span" className={classes.badge}>
                  {featured.type === 'new' && (
                    <>
                      <Star />
                      {getLiteral('2037')}
                    </>
                  )}
                  {featured.type === 'default' && 'hot'}
                </Typography>
              )}
              {featured && (
                <Typography
                  variant="caption"
                  component="span"
                  className={classes.badge}
                  style={{ background: '#fffbc8', color: '#222' }}
                >
                  <Whatshot />
                  {getLiteral('1948')}
                </Typography>
              )}
            </Grid>

            <Typography variant="body1" data-cy="productName" className={classes.text}>
              {name}
            </Typography>

            <a className={classes.seeMoreDetails} onClick={(e) => handleCheckGeolocation(e, product)}>
              <Typography component="span" style={{ fontSize: 12 }}>
                {getLiteral('2018')}
              </Typography>
              <ArrowForwardIos color="primary" style={{ fontSize: 14 }} />
            </a>
          </Grid>
        </Grid>
        <Grid>
          <Button className={classes.buttonSearch}>
            <Search color="primary" />
            <Typography component="span">{getLiteral('1993')}</Typography>
          </Button>
        </Grid>
      </ListItem>
      <Divider />
    </>
  )
}

export default memo(Product)
